/**
 * OCC Benchmark 报表的「白话说明」：给没有 OCC 背景的读者看的指标解释、阅读指南与热力图读法。
 *
 * - ``HelpLabel``：标签 + 问号图标，hover 出白话解释（表头 / KPI 卡片共用）；
 * - ``buildMetricHelp``：各指标的白话解释（匹配帧的时间容差取自后端 summary）；
 * - ``ReadingGuide``：概览页顶部「怎么看这份报告」——由粗到细的分层表 + 阅读顺序；
 * - ``HeatmapReadingNote``：热力图下方的读法说明。
 *
 * 只做展示：所有数值都直接取自后端响应，不重新计算、也不按阈值打「好 / 差」标签。
 */
import { useMemo, useState } from 'react';
import { Button, Table, Tooltip } from 'antd';
import { DownOutlined, QuestionCircleOutlined, UpOutlined } from '@ant-design/icons';

import type { OccBenchmarkCase, OccHaloHeadline } from '@/apis/simulation';

import { EMPTY, fmtCount, fmtRatio } from '../format';

export type OccMetricKey = keyof OccHaloHeadline;

/** 匹配帧时间容差缺失时的展示默认值（与 halo occ_benchmark 默认一致）。 */
const DEFAULT_TOLERANCE_SEC = 0.05;

/** 仿真 OCC 覆盖范围（sim_footprint）的白话描述，热力图读法与说明页共用。 */
export const COVERAGE_TEXT = '约车前 41 m、车后 10 m、左右各 10 m';

// ───────────────────────────── HelpLabel ─────────────────────────────────────

interface HelpLabelProps {
  label: React.ReactNode;
  help?: React.ReactNode;
}

/** 标签 + 小问号；没有 help 时只渲染标签。点击问号不触发表头排序。 */
export function HelpLabel({ label, help }: HelpLabelProps) {
  if (!help) {
    return <>{label}</>;
  }
  return (
    <span className="occ-help-label">
      {label}
      <Tooltip title={help} classNames={{ root: 'occ-help-tip' }}>
        <QuestionCircleOutlined className="occ-help-icon" onClick={(e) => e.stopPropagation()} />
      </Tooltip>
    </span>
  );
}

// ───────────────────────────── 指标白话 ───────────────────────────────────────

export type OccHelpKey = OccMetricKey | 'frames' | 'share' | 'caseId' | 'apiCode' | 'sceneGroup';

const IOU_EXAMPLE = '例：真值 100 格、仿真 120 格、重合 80 格 → 80 ÷ (100 + 120 − 80) = 57%。';
const HIGHER_BETTER = '越高越好。';

/** 各指标 / 列的白话解释。tol 为匹配帧的时间容差（秒）。 */
export function buildMetricHelp(tolSec: number | null | undefined): Record<OccHelpKey, React.ReactNode> {
  const tol = tolSec ?? DEFAULT_TOLERANCE_SEC;
  return {
    caseId: '场景编号：一段路测录像截出来的一个评测片段。',
    apiCode: '场景来源编号，可点击跳转到 Jira / Case-Flow 查看原始问题或录像。',
    sceneGroup: '场景所属的分组（如路口、窄路等），用于按场景类型看分布。',
    frames: `真值（标准答案）大约每秒 1 帧；只有能和时间相差 ≤${tol} s 的一帧仿真 OCC 配上对的真值帧才参与比较，配上的就叫「匹配帧」。为 0 表示这个场景没有参与汇总。`,
    share: '该场景匹配帧 ÷ 所有场景匹配帧之和，也就是它在汇总值里的权重：帧越多，对汇总值的影响越大。',
    occupancyIou: `不看类别，只看每个格子里「有没有东西」（路面也算有东西）。IoU（重合度）= 两边都有东西的格子 ÷ 任意一边有东西的格子。${HIGHER_BETTER}`,
    statusMiou: `把 12 种类别合成 4 种状态：空白 / 能走（路面）/ 被固定物体挡住 / 被车·人等会动的东西挡住，每种状态算一次重合度（IoU），再取平均。${HIGHER_BETTER}`,
    miou: (
      <>
        每个类别算一次 IoU（重合度）= 两边都是这一类的格子 ÷ 任意一边是这一类的格子，再对各类别取平均。
        {IOU_EXAMPLE}仿真不输出的类别得 0，会拉低平均。{HIGHER_BETTER}
      </>
    ),
    godF1: `把相连的同类格子圈成一个个障碍物，真值与仿真一一配对，重合度 ≥0.20 算「找到」。F1 综合召回率（真值障碍物被找到的比例）和精确率（仿真报的障碍物里对的比例）。这里所有障碍物混在一起算，车最多，所以主要由车决定。${HIGHER_BETTER}`,
    godRecall: `真值里的障碍物被仿真找到（重合度 ≥0.20）的比例。${HIGHER_BETTER}`,
    godPrecision: `仿真报出的障碍物里，确实对应真值障碍物的比例。${HIGHER_BETTER}`,
    godMf1: `同 Instance GOD，但每个类别分开算 F1 再取平均：行人、锥桶和车一样重要。${HIGHER_BETTER}`,
    macroRayiou: `从车向四周发射线，看每条射线上第一个碰到的东西类别是否一致、距离是否在容忍范围内（max(0.5 m, 10% × 距离)），每个类别算重合度后取平均（不含路面）。${HIGHER_BETTER}`,
    letrayF1lFront: `从车向四周发 720 条射线（这里只看车前方的扇区、不分类别），看每条射线上第一个障碍物的位置是否在距离容忍内（max(0.5 m, 10% × 距离)）。F1L 同时考虑「找没找到」和「距离准不准」，距离越接近得分越高。${HIGHER_BETTER}`,
    letrayRayIouFront: `车前方扇区内，射线上第一个障碍物在容忍距离内对上的射线 ÷（真值有障碍物的射线 + 仿真有障碍物的射线 − 对上的射线），不分类别。${HIGHER_BETTER}`,
    letrayMf1l: `把 LET-Ray F1L 按类别分开算（车前方扇区），再对真值里出现过的类别取平均：小类和车一样重要。${HIGHER_BETTER}`,
  };
}

/** 白话 + 技术口径两段式 tooltip 内容。 */
export const withTech = (plain: React.ReactNode, tech?: string): React.ReactNode =>
  tech ? (
    <>
      <div>{plain}</div>
      <div className="occ-help-tech">口径：{tech}</div>
    </>
  ) : (
    plain
  );

// ───────────────────────────── 阅读指南 ───────────────────────────────────────

interface GuideMetric {
  key: OccMetricKey;
  label: string;
}

interface GuideRow {
  level: string;
  metrics: GuideMetric[];
  question: string;
  lowMeans: string;
}

const guideRows = (vision: boolean): GuideRow[] => [
  {
    level: '① 有没有东西',
    metrics: [{ key: 'occupancyIou', label: '占据 IoU' }],
    question: '不管是什么，只看格子里有没有东西（路面也算「有东西」）：仿真涂了的格子和真值涂了的格子重合多少？',
    lowMeans: '该有东西的地方没涂，或者空地被涂上了——连「哪里有东西」的大轮廓都对不上。',
  },
  {
    level: '② 能不能走',
    metrics: [{ key: 'statusMiou', label: 'status mIoU' }],
    question:
      '把 12 种类别合成 4 种状态：空白 / 能走（路面）/ 被固定物体挡住 / 被车·人等会动的东西挡住，看这 4 种状态分得对不对。',
    lowMeans: '能走和被挡住的区域分错，或者把会动的东西当成固定物体（反之亦然）。',
  },
  vision
    ? {
        level: '③ 是什么东西',
        metrics: [{ key: 'macroRayiou', label: 'Macro RayIoU' }],
        question: '从车向四周看出去，每个方向上第一个碰到的东西，类别对不对、距离准不准？按类别分别打分再平均。',
        lowMeans: '看到的第一个东西类别分错，或者距离偏差超出容忍；仿真不输出的类别得 0，也会拉低平均。',
      }
    : {
        level: '③ 是什么东西',
        metrics: [{ key: 'miou', label: '语义 mIoU' }],
        question: '每个格子涂的具体类别（车、行人、路沿、植被……）对不对？每个类别算重合度后取平均。',
        lowMeans: '类别分错、涂得太满（把周围空地也涂成该类），或者仿真根本不输出某些类别（这些类得 0，会把平均拉低）。',
      },
  vision
    ? {
        level: '④ 障碍物检测准不准',
        metrics: [
          { key: 'letrayF1lFront', label: 'LET-Ray F1L（前向）' },
          { key: 'letrayMf1l', label: 'LET-Ray mF1L' },
        ],
        question: '从车向前方发射线，每条射线上第一个障碍物有没有找到、距离准不准（容忍 max(0.5 m, 10% × 距离)）？',
        lowMeans: '前方障碍物漏报、多报，或距离偏差超出容忍。F1L 尚可而 mF1L 低 → 不分类别时位置大体对，但具体类别分错或小类做得差。',
      }
    : {
        level: '④ 障碍物检测准不准',
        metrics: [
          { key: 'godF1', label: 'Instance GOD F1@0.20' },
          { key: 'godMf1', label: 'god_mF1@0.20' },
        ],
        question: '把相连的同类格子圈成一个个障碍物：真值里的障碍物有没有被找到？仿真报的障碍物是不是真的？',
        lowMeans: '障碍物漏报、多报，或形状 / 位置对不上（重合度不到 0.20）。F1 尚可而 mF1 低 → 车做得还行，但行人、锥桶等小类差。',
      },
];

interface ReadingGuideProps {
  headline: OccHaloHeadline;
  cases: OccBenchmarkCase[];
  vision: boolean;
  help: Record<OccHelpKey, React.ReactNode>;
}

interface Range {
  min: number;
  max: number;
  n: number;
}

/** 对有匹配帧的 case 求某指标 min~max（null 不计）；没有可用值时返回 null。 */
export function metricRange(cases: OccBenchmarkCase[], key: OccMetricKey): Range | null {
  let min = Infinity;
  let max = -Infinity;
  let n = 0;
  for (const c of cases) {
    if (!(c.frames > 0)) {
      continue;
    }
    const v = c.metrics?.[key];
    if (v === null || v === undefined) {
      continue;
    }
    min = Math.min(min, v);
    max = Math.max(max, v);
    n += 1;
  }
  return n ? { min, max, n } : null;
}

const fmtRange = (r: Range | null): string => (r ? `${fmtRatio(r.min, 1)} ~ ${fmtRatio(r.max, 1)}` : EMPTY);

/** 概览页顶部「怎么看这份报告」。可折叠，默认展开。 */
export function ReadingGuide({ headline, cases, vision, help }: ReadingGuideProps) {
  const [open, setOpen] = useState(true);
  const rows = useMemo(() => guideRows(vision), [vision]);
  const zeroFrameCases = cases.filter((c) => !(c.frames > 0)).length;
  const usedCases = cases.length - zeroFrameCases;

  return (
    <div className="pe-report-card occ-guide">
      <div className="pe-report-card-title occ-guide-title">
        怎么看这份报告
        <Button
          type="link"
          size="small"
          className="occ-guide-toggle"
          icon={open ? <UpOutlined /> : <DownOutlined />}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? '收起' : '展开'}
        </Button>
      </div>
      {open && (
        <>
          <p className="occ-guide-intro">
            把车周围的地面切成 <b>10 cm 一格的棋盘</b>：<b>真值</b>是标准答案（每个格子涂一种类别颜色，比如路面、车、行人），
            <b>仿真输出的 OCC</b> 是答卷。下面每个指标都从不同角度给「答卷和标准答案像不像」打分，范围 0~100%，
            <b>都是越高越好</b>。建议按表格从上往下、由粗到细地看；鼠标移到指标名旁的
            <QuestionCircleOutlined className="occ-help-icon" />上可看白话解释。
          </p>
          <Table<GuideRow>
            rowKey="level"
            size="small"
            dataSource={rows}
            pagination={false}
            tableLayout="fixed"
            scroll={{ x: 980 }}
            className="occ-guide-table"
            columns={[
              { title: '层级', key: 'level', width: 120, render: (_, r) => <b>{r.level}</b> },
              {
                title: '看哪个指标',
                key: 'metric',
                width: 170,
                render: (_, r) => (
                  <div className="occ-guide-stack">
                    {r.metrics.map((m) => (
                      <HelpLabel key={m.key} label={m.label} help={help[m.key]} />
                    ))}
                  </div>
                ),
              },
              {
                title: <HelpLabel label="汇总值" help="所有有匹配帧的场景合在一起算出的值（计数先加总再算比例，不是各场景的平均）。" />,
                key: 'value',
                width: 100,
                align: 'right',
                render: (_, r) => (
                  <div className="occ-guide-stack occ-guide-num">
                    {r.metrics.map((m) => (
                      <b key={m.key}>{fmtRatio(headline[m.key])}</b>
                    ))}
                  </div>
                ),
              },
              {
                title: (
                  <HelpLabel
                    label="各场景范围"
                    help={`每个场景单独算出的最低 ~ 最高值（只统计匹配帧 > 0 的 ${usedCases} 个场景）。范围很宽说明不同场景差别大，可到「2 评测集分布」里逐个看。`}
                  />
                ),
                key: 'range',
                width: 150,
                align: 'right',
                render: (_, r) => (
                  <div className="occ-guide-stack occ-guide-num">
                    {r.metrics.map((m) => (
                      <span key={m.key}>{fmtRange(metricRange(cases, m.key))}</span>
                    ))}
                  </div>
                ),
              },
              { title: '它回答什么问题', key: 'q', render: (_, r) => r.question },
              { title: '数值低说明什么', key: 'low', render: (_, r) => r.lowMeans },
            ]}
          />
          <div className="occ-guide-sub">阅读顺序与结论</div>
          <ol className="occ-guide-list">
            <li>先看粗（① 占据 IoU），再看细（③ 语义、④ 障碍物）。</li>
            <li>
              粗的好、细的差 → 模型大体知道哪里有东西，但类别分错、涂得太满，或者障碍物的形状 / 位置对不上。
            </li>
            <li>
              也要看「匹配帧」：为 0 的场景没有参与汇总
              {zeroFrameCases > 0
                ? `（本次有 ${fmtCount(zeroFrameCases)} 个这样的场景，共 ${fmtCount(cases.length)} 个）`
                : '（本次所有场景都有匹配帧）'}
              。
            </li>
            <li>
              汇总值不是各场景数值的平均：是把所有场景的格子 / 射线 / 障碍物计数先加起来再算比例，所以帧多的场景权重大
              （就是「帧占比」），汇总值不一定落在各场景范围的正中间。
            </li>
            <li>每个指标的完整解释和公式见「3 Metrics 说明」页签；各指标的空间分布见 4~6 页签里的热力图。</li>
          </ol>
        </>
      )}
    </div>
  );
}

// ───────────────────────────── 热力图读法 ─────────────────────────────────────

/** 热力图下方的读法说明。metric 为该图的指标名。 */
export function HeatmapReadingNote({ metric }: { metric?: string }) {
  return (
    <ul className="occ-heat-note">
      <li>
        每格是车周围 5 m × 5 m 区域内、所有场景所有匹配帧合起来算的{metric ? `「${metric}」` : '该指标'}；
        <b>颜色越深越好</b>，格中数字是百分比。
      </li>
      <li>从上到下看「越远越差」，从中间往两边看「路中 vs 路边」，也可以左右对比（图的左边就是车的左边）。</li>
      <li>空白格 = 不在仿真 OCC 覆盖范围（{COVERAGE_TEXT}），不评测，不是 0。</li>
      <li>左右最外侧有数据的一列只有贴着覆盖边界的很窄一条，数值容易极端（如 0），参考意义不大。</li>
    </ul>
  );
}
